import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ImageMask } from 'elements/visual/ImageMask';
import { motion } from 'framer-motion';
import { useGoToMarker } from 'hooks/map/useGoToMarker';
import {
    ANIMATION_BIGGER,
    ANIMATION_SMALLER,
    conditionalScaleAnimation
} from 'libs/styles/animations';
import { EColors, EFonts, EGrads, EHovers } from 'libs/styles/styling';
import { getIconForTool } from 'libs/utils/tools/toolsUtils';
import { useContext, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import styled, { css } from 'styled-components/macro';

interface Props {
    markerId: string;
    odd: boolean;
    className?: string;
}

export const LayerItem = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const marker = ctxEdit.worldMarkers.find((e) => e.id === props.markerId);
    const goToMarker = useGoToMarker({
        mapRef: ctxEdit.mapRef,
        defaultZoom: ctxEdit.map?.showOnMapZoom,
        padding: 200,
        animate: true,
        bearing: ctxEdit.map?.initial?.rotation || 0
    });
    const isSelected = ctxEdit.selectedMarkers.includes(marker?.id);
    const isLockVisible = isSelected || !marker?.selectable;
    const isEyeVisible = isSelected || !marker?.visible;

    const getInitialLayerName = () => {
        if (!marker) {
            return '';
        }
        if (marker?.layerTitle) {
            return marker.layerTitle;
        }
        if (marker.elementType === 'v2/text' && marker.elementData.generatedText) {
            return marker.elementData.generatedText.substring(0, 20);
        }
        if (marker.elementType === 'v2/icon' && marker.elementData.text) {
            return marker.elementData.text.substring(0, 20);
        }
        if (marker.elementType === 'v2/variant_icon' && marker.elementData.text) {
            return marker.elementData.text.substring(0, 20);
        }
        if (marker.elementType === 'indoor_stand' && marker.elementData.text) {
            return marker.elementData.text.substring(0, 20);
        }
        return marker.elementType;
    };

    const [layerName, setLayerName] = useState(getInitialLayerName());

    if (!marker) {
        return null;
    }

    function renameLayer(value: string) {
        ctxEdit.updateWorldMarker(marker.id, {
            layerTitle: value
        });
    }

    function locateMarker() {
        goToMarker(marker);
    }

    function toggleSelectable(e) {
        e.preventDefault();
        e.stopPropagation();
        ctxEdit.updateWorldMarker(marker.id, {
            selectable: !marker.selectable
        });
    }

    function toggleVisibility(e) {
        e.preventDefault();
        e.stopPropagation();
        ctxEdit.updateWorldMarker(marker.id, {
            visible: !marker.visible
        });
    }

    return (
        <S_LayerItem className={props.className} $isSelected={isSelected} $odd={props.odd}>
            <S_Left>
                <S_Icon
                    icon={['fal', getIconForTool(marker.elementType)]}
                    $isSelected={isSelected}
                />
                {!isSelected && <S_Name>{getInitialLayerName()}</S_Name>}
                {isSelected && (
                    <S_Input
                        value={layerName}
                        onBlur={() => {
                            renameLayer(layerName);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                (e.target as any).blur();
                            }
                        }}
                        onChange={(e) => {
                            setLayerName(e.target.value);
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    />
                )}
            </S_Left>
            <S_Right>
                {isSelected && (
                    <motion.div
                        {...conditionalScaleAnimation(true, ANIMATION_BIGGER, ANIMATION_SMALLER)}
                    >
                        <S_LocateMarkerIcon icon={['fal', 'crosshairs']} onClick={locateMarker} />
                    </motion.div>
                )}
                {!isSelected && marker['stories']?.length > 0 && (
                    <S_Interactive icon={['fal', 'bullseye-pointer']} />
                )}
                {isEyeVisible && (
                    <S_Eye
                        icon={['fal', marker.visible ? 'eye' : 'eye-slash']}
                        onClick={toggleVisibility}
                        $isSelected={isSelected}
                    />
                )}
                {isLockVisible && (
                    <S_Lock
                        icon={['fal', marker.selectable ? 'lock-open' : 'lock']}
                        onClick={toggleSelectable}
                        $isSelected={isSelected}
                    />
                )}
            </S_Right>
            {isSelected && (
                <ImageMask randomizePosition={isSelected} step={20} image="contour" repeat={true} />
            )}
        </S_LayerItem>
    );
};

const S_LayerItem = styled.div<{ $isSelected: boolean; $odd: boolean }>`
    height: 35px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    user-select: none;
    overflow: hidden;
    white-space: nowrap;
    ${(props) => {
        if (props.$isSelected) {
            return css`
                background: ${EGrads.ACC2_GRAD};
            `;
        } else {
            return css`
                cursor: pointer;
                background: ${props.$odd ? '#EFEFEF' : '#F3F3F3'};
                &:hover {
                    box-shadow: ${EHovers.LIGHT1};
                }
            `;
        }
    }}
    &:hover {
        background-color: ${EColors.GREY5};
    }
`;

const S_Left = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const S_Icon = styled(FontAwesomeIcon)<{ $isSelected: boolean }>`
    font-size: 15px;
    color: ${(props) => (props.$isSelected ? EColors.WHITE : '#141C54')};
    margin: 0 10px 0 0;
    width: 14px;
`;

const S_Name = styled.div`
    text-transform: capitalize;
    color: #141c54;
    font-size: 12px;
    font-family: ${EFonts.Regular};
    padding: 0px 2px;
`;

const S_Right = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const S_LocateMarkerIcon = styled(FontAwesomeIcon)`
    font-size: 15px;
    color: ${EColors.WHITE};
    cursor: pointer;
    width: 17px;
    text-align: center;
`;

const S_Input = styled.input`
    text-transform: capitalize;
    color: ${EColors.WHITE};
    font-size: 12px;
    font-family: ${EFonts.Regular};
    background: transparent;
    border: none;
    outline: none;
    width: 100px;
    &:focus {
        box-shadow: ${EHovers.LIGHT2};
    }
    &:hover {
        box-shadow: ${EHovers.LIGHT2};
    }
`;

const S_Interactive = styled(FontAwesomeIcon)`
    font-size: 15px;
    color: ${EColors.GREY_EDITOR};
    width: 17px;
    text-align: center;
`;

const S_Lock = styled(FontAwesomeIcon)<{ $isSelected: boolean }>`
    font-size: 15px;
    color: ${(props) => (props.$isSelected ? EColors.WHITE : EColors.GREY_EDITOR)};
    cursor: pointer;
    width: 17px;
    text-align: center;
`;

const S_Eye = styled(FontAwesomeIcon)<{ $isSelected: boolean }>`
    font-size: 15px;
    color: ${(props) => (props.$isSelected ? EColors.WHITE : EColors.GREY_EDITOR)};
    cursor: pointer;
    width: 17px;
    text-align: center;
`;
